<template>
  <v-container :fluid="$vuetify.display.mdAndDown">
    <v-container>
      <alert-card
          v-if="alert.visible === true"
          v-model:visible="alert.visible"
          :type="alert.type"
          :title="alert.title"
          :text="alert.text"
          :closable="false"
          class="pa-0"
      />
    </v-container>
    <payment-details
        :balances="accountStatistics.balances"
        :requests-finished="accountStatistics.requestsFinished"
        :loading="loadingStatistic"
    >
      <account-client-info-skeleton v-if="loadingUserData"/>
      <accountant-client-info
          v-else
          :name="name"
          :username="username"
          :default-currency="defaultCurrency"
      />
      <v-divider/>
    </payment-details>
    <v-container>
      <v-divider/>
      <accountant-card
          class="my-4"
          :type-options="types"
          :currency-options="currencies"
          v-model:type="type"
          v-model:amount="amount"
          v-model:currency="currency"
          v-model:description="description"
          v-model:loading="loadingNewPayment"
          v-model:valid="valid"
          :save-callback="createPayment"
      />

      <payment-list
          :items="paymentList"
          :loading="loadingPayments"
          :skeleton-amount="limit"
          class="mt-4"
      />
      <v-pagination
          v-model="this.page"
          :length="this.pageAmount"
      />
    </v-container>
  </v-container>
</template>

<script>
import PaymentDetails from "@/components/Payment/PaymentDetails.vue";
import PaymentList from "@/components/Payment/PaymentList.vue";
import {inject} from "vue";
import {auth} from "@/common/auth";
import {router} from "@/router";
import {api} from "@/common/api";
import AccountantCard from "@/components/Accountant/AccountantCard.vue";
import {PaymentType} from "@/common/enum/payment_type";
import {Currency} from "@/common/enum/currency";
import AccountantClientInfo from "@/components/Accountant/AccountantClientInfo.vue";
import AccountClientInfoSkeleton from "@/components/Accountant/AccountClientInfoSkeleton.vue";
import {dates} from "@/common/dates";
import AlertCard from "@/components/AlertCard.vue";
import {messages} from "@/common/enum/messages";
import {CurrencySymbols} from "@/common/enum/currency_symbols";
import {numbers} from "@/common/numbers";

export default {
  name: "AccountantClientView",
  components: {AlertCard, AccountClientInfoSkeleton, AccountantClientInfo, AccountantCard, PaymentList, PaymentDetails},
  props: {
    id: String
  },
  data() {
    return {
      types: [],
      currencies: [],
      page: 1,
      total: 1,
      limit: 10,
      accountStatistics: {},
      type: null,
      amount: null,
      currency: null,
      description: null,
      valid: true,
      name: null,
      username: null,
      defaultCurrency: null,
      paymentList: [],
      paymentsRequestState: null,
      loadingStatistic: false,
      loadingUserData: false,
      loadingPayments: false,
      loadingNewPayment: false,
      alert: {
        initiator: null,
        visible: false,
        type: 'error',
        title: 'Error',
        text: ''
      }
    }
  },
  computed: {
    pageAmount() {
      return Math.ceil(this.total / this.limit)
    }
  },
  setup() {
    return {
      switchAuthorizationState: inject('authorization').switchState
    }
  },
  mounted() {
    this.loadTypes()
    this.loadCurrencies()
    this.loadUser()
    this.loadAccountStatisticsById()
    this.loadPaymentList()
  },
  methods: {
    loadTypes() {
      this.types = Object.entries(PaymentType).reduce((type, [key, value]) => {
        type.push({'value': key, 'title': value})
        return type
      }, [])
    },
    loadCurrencies() {
      this.currencies = Object.entries(Currency).reduce((type, [key, value]) => {
        type.push({'value': key, 'title': value})
        return type
      }, [])
    },
    loadUser() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      this.loadingUserData = true
      api.getUser(token, this.id, this.setUserData)
    },
    setUserData(response) {
      if (response?.data?.status === 'error') {
        this.alert.initiator = 'user'
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.accountant_user_view.user_loading_error : response.data.message
        return
      }

      this.resetAlert('user')
      this.loadingUserData = false
      const user = response.data

      this.username = user.username
      this.name = user.profile !== null && user.profile.first_name !== null && user.profile.last_name !== null ? user.profile.first_name + ' ' + user.profile.last_name : 'Not provided'
      this.defaultCurrency = user.profile !== null && user.profile.currency !== null ? user.profile.currency : Currency.USD
    },
    loadPaymentList() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.paymentsRequestState && !this.paymentsRequestState.signal.aborted) {
        this.paymentsRequestState.abort()
      }

      this.paymentsRequestState = new AbortController()
      this.loadingPayments = true
      api.getPayments(token, {
        page: this.page,
        limit: this.limit,
        user_id: this.id
      }, this.paymentsRequestState.signal, this.setPayments)
    },
    setPayments(response) {
      this.loadingPayments = false
      if (response?.data?.status === 'error') {
        this.alert.initiator = 'payment'
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.accountant_user_view.payments_loading_error : response.data.message
        return
      }

      this.resetAlert('payment')
      const payments = response.data.payments
      this.total = response.data.total
      this.paymentList = payments.map(payment => {
        return {
          id: payment.id,
          type: PaymentType[payment.type],
          amount: numbers.formatMoney(payment.value, payment.currency),
          description: payment.description,
          date: dates.mapToDate(payment.created_at),
        }
      });
    },
    loadAccountStatisticsById() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      this.loadingStatistic = true
      api.getAccountStatisticsById(token, this.id, this.setAccountStatistics)
    },
    setAccountStatistics(response) {
      if (response?.data?.status === 'error') {
        this.alert.initiator = 'statistic'
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.accountant_user_view.statistic_loading_error : response.data.message
        return
      }

      this.resetAlert('statistic')
      this.loadingStatistic = false
      const statistics = response.data

      this.accountStatistics = {
        balances: statistics.balances.map(balance => {
          return {
            balance: balance.balance_amount,
            hold: balance.hold_amount,
            spent: balance.debit_amount,
            currency: CurrencySymbols[balance.currency]
          }
        }),
        requestsFinished: statistics.finished_requests_amount
      }
    },
    createPayment() {
      const token = auth.getToken()
      if (token == null) {
        this.switchAuthorizationState()
        router.push({name: 'sign-in', state: {redirectTo: 'home'}})
        return
      }

      if (this.valid !== true) {
        return
      }

      this.loadingNewPayment = true
      api.createPayments(token, {
        user_id: this.id,
        type: this.type,
        amount: this.amount,
        currency: this.currency,
        description: this.description
      }, this.reloadData)
    },
    reloadData(response) {
      this.loadingNewPayment = false
      if (response?.data?.status === 'error') {
        this.alert.initiator = 'payment_creation'
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.accountant_user_view.payment_creation_error : response.data.message
        return
      }

      this.resetAlert('payment_creation')
      this.loadPaymentList()
      this.loadAccountStatisticsById()
    },
    resetAlert(initiator) {
      if (this.alert.initiator === initiator) {
        this.alert.visible = false
      }
    }
  }
}
</script>

<style scoped>

</style>